import { AnchorButton, Collapse } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectClassMappingRelationshipRow } from 'src/containers/ProjectMapping/ProjectClassMappingRelationshipSection/ProjectClassMappingRelationshipRow';
import { projectClassRelationshipsSelector } from 'src/selectors/projectClassRelationships';
import { projectMappingRelationshipFilterItemsSelector } from 'src/selectors/projectMappingRelationshipFilters';
import { MappingFilterCheckState } from 'src/store/ui/constants';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectClassMappingRelationshipsSection: React.VFC = () => {
  const { projectId } = useRouterParams();
  const mapping = useMappingFromRoute() as Mapping & { to: string };

  const relationshipsByIri = useReduxSelector(
    projectClassRelationshipsSelector(
      projectId,
      getModelConceptClassValue(mapping.to)
    )
  );

  const [isOpen, setIsOpen] = React.useState(true);

  const { allRelationshipItems } = useReduxSelector(
    projectMappingRelationshipFilterItemsSelector(
      projectId,
      getModelConceptClassValue(mapping.to),
      mapping.value
    )
  );

  const relationships = React.useMemo(
    () =>
      Object.values(relationshipsByIri)
        // only show relationships here with domains and ranges
        .filter((relationship) => {
          const hasDomainAndRange = Boolean(
            relationship.domains.size && relationship.ranges.size
          );
          const isVisible = Boolean(
            allRelationshipItems.filter(
              (relationshipItem) =>
                relationshipItem.value === relationship.iri &&
                relationshipItem.checkState === MappingFilterCheckState.CHECKED
            ).length
          );
          return hasDomainAndRange && isVisible;
        })
        .sort(alphaSort((relationship) => relationship.label)),
    [allRelationshipItems, relationshipsByIri]
  );

  const rows = relationships.flatMap((relationship, index) => {
    const isMappingClassSource =
      relationship.domains.has(getModelConceptClassValue(mapping.to)) ||
      Array.from(relationship.sourceClassIris).some((sourceClassIri) =>
        relationship.domains.has(sourceClassIri)
      );
    const isMappingClassTarget =
      relationship.ranges.has(getModelConceptClassValue(mapping.to)) ||
      Array.from(relationship.sourceClassIris).some((sourceClassIri) =>
        relationship.ranges.has(sourceClassIri)
      );

    if (isMappingClassSource && isMappingClassTarget) {
      return [
        <ProjectClassMappingRelationshipRow
          key={`${relationship.iri}-${RelationshipDirection.OUTGOING}`}
          direction={RelationshipDirection.OUTGOING}
          index={index}
          relationship={relationship}
        />,
        <ProjectClassMappingRelationshipRow
          key={`${relationship.iri}-${RelationshipDirection.INCOMING}`}
          direction={RelationshipDirection.INCOMING}
          index={index}
          relationship={relationship}
        />,
      ];
    }
    const direction = isMappingClassSource
      ? RelationshipDirection.OUTGOING
      : RelationshipDirection.INCOMING;
    return (
      <ProjectClassMappingRelationshipRow
        key={`${relationship.iri}-${direction}`}
        direction={direction}
        index={index}
        relationship={relationship}
      />
    );
  });

  return (
    <div className="sd-project-mapping-collapse">
      <div className="sd-project-mapping-collapse-header">
        <AnchorButton
          disabled={!rows.length}
          icon={isOpen ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_UP}
          minimal
          onClick={() => setIsOpen(!isOpen)}
          text={copy.components.projectMapping.relationships.section}
        />
      </div>
      <Collapse
        className="sd-project-mapping-collapse-grid--suggestions"
        isOpen={isOpen}
      >
        {rows}
      </Collapse>
    </div>
  );
};
